import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Progress } from '../ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  ArrowRight, 
  ArrowLeft, 
  Check, 
  Upload, 
  FileSpreadsheet, 
  Mail, 
  Download,
  Eye,
  Send,
  AlertCircle,
  User,
  Calendar,
  FileText,
  Shield,
  Loader2
} from 'lucide-react';
import { 
  CredentialSchema, 
  CredentialTemplate, 
  IssuanceRequest, 
  BulkIssuanceRequest,
  W3CVerifiableCredential,
  W3CIssuer,
  OrganizationIssuerConfig
} from '../../types/W3CCredentialTypes';

interface CredentialCreationWizardProps {
  onComplete: (result: any) => void;
  onCancel: () => void;
}

export default function CredentialCreationWizard({ onComplete, onCancel }: CredentialCreationWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [issuanceType, setIssuanceType] = useState<'manual' | 'bulk'>('manual');
  const [selectedSchema, setSelectedSchema] = useState<CredentialSchema | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<CredentialTemplate | null>(null);
  const [credentialData, setCredentialData] = useState<Record<string, any>>({});
  const [recipientInfo, setRecipientInfo] = useState({
    name: '',
    email: '',
    sendNotification: true
  });
  const [bulkData, setBulkData] = useState({
    csvFile: null as File | null,
    csvContent: '',
    parsedData: [] as any[]
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [previewCredential, setPreviewCredential] = useState<W3CVerifiableCredential | null>(null);

  // For now, use empty arrays until real API is implemented
  const mockSchemas: CredentialSchema[] = [];
  const mockTemplates: CredentialTemplate[] = [];

  const mockIssuerConfig: OrganizationIssuerConfig = {
    id: 'issuer_1',
    organizationId: 'org1',
    issuerDid: 'did:web:university.edu',
    issuerName: 'University of Example',
    issuerUrl: 'https://university.edu',
    issuerEmail: 'credentials@university.edu',
    issuerImage: 'https://university.edu/logo.png',
    keyType: 'Ed25519',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  };

  const steps = [
    'Select Type',
    'Choose Schema',
    'Select Template',
    issuanceType === 'manual' ? 'Enter Data' : 'Upload Data',
    'Review & Issue'
  ];

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 1:
        if (!selectedSchema) {
          newErrors.schema = 'Please select a credential schema';
        }
        break;
      case 2:
        if (!selectedTemplate) {
          newErrors.template = 'Please select a template';
        }
        break;
      case 3:
        if (issuanceType === 'manual') {
          if (!recipientInfo.name) {
            newErrors.recipientName = 'Recipient name is required';
          }
          if (!recipientInfo.email) {
            newErrors.recipientEmail = 'Recipient email is required';
          }
          // Validate schema fields
          selectedSchema?.fields.forEach(field => {
            if (field.required && !credentialData[field.name]) {
              newErrors[field.name] = `${field.displayName} is required`;
            }
          });
        } else {
          if (!bulkData.csvFile) {
            newErrors.csvFile = 'Please upload a CSV file';
          }
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleCsvUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const csvContent = e.target?.result as string;
        setBulkData(prev => ({ ...prev, csvFile: file, csvContent }));
        
        // Parse CSV
        const lines = csvContent.split('\n');
        const headers = lines[0].split(',').map(h => h.trim());
        const parsedData = lines.slice(1).map(line => {
          const values = line.split(',').map(v => v.trim());
          const row: any = {};
          headers.forEach((header, index) => {
            row[header] = values[index];
          });
          return row;
        }).filter(row => Object.values(row).some(val => val)); // Remove empty rows
        
        setBulkData(prev => ({ ...prev, parsedData }));
      };
      reader.readAsText(file);
    }
  };

  const generateCredential = (data: Record<string, any>, recipientEmail: string): W3CVerifiableCredential => {
    const now = new Date().toISOString();
    const credentialId = `https://university.edu/credentials/${Date.now()}`;
    
    const issuer: W3CIssuer = {
      id: mockIssuerConfig.issuerDid,
      name: mockIssuerConfig.issuerName,
      url: mockIssuerConfig.issuerUrl,
      email: mockIssuerConfig.issuerEmail
    };

    const credentialSubject = {
      id: `mailto:${recipientEmail}`,
      ...data
    };

    return {
      '@context': [
        'https://www.w3.org/2018/credentials/v1',
        'https://www.w3.org/2018/credentials/examples/v1'
      ],
      id: credentialId,
      type: ['VerifiableCredential', selectedSchema?.name.replace(/\s+/g, '') + 'Credential' || 'CustomCredential'],
      issuer,
      issuanceDate: now,
      credentialSubject
    };
  };

  const handleIssueCredential = async () => {
    setIsProcessing(true);
    
    try {
      if (issuanceType === 'manual') {
        const credential = generateCredential(credentialData, recipientInfo.email);
        setPreviewCredential(credential);
        
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        onComplete({
          type: 'manual',
          credential,
          recipient: recipientInfo,
          template: selectedTemplate
        });
      } else {
        // Bulk issuance
        const credentials = bulkData.parsedData.map(row => 
          generateCredential(row, row.email || row.recipientEmail)
        );
        
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        onComplete({
          type: 'bulk',
          credentials,
          count: credentials.length,
          template: selectedTemplate
        });
      }
    } catch (error) {
      console.error('Credential issuance failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const generateCsvTemplate = () => {
    if (!selectedSchema) return;
    
    const headers = selectedSchema.fields.map(field => field.name);
    const sampleRow = selectedSchema.fields.map(field => {
      switch (field.type) {
        case 'text':
          return `Sample ${field.displayName}`;
        case 'number':
          return '42';
        case 'date':
          return '2024-01-01';
        case 'email':
          return 'recipient@example.com';
        default:
          return `Sample ${field.displayName}`;
      }
    });
    
    const csvContent = [headers.join(','), sampleRow.join(',')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedSchema.name.replace(/\s+/g, '_')}_template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const availableTemplates = mockTemplates.filter(template => 
    template.schemaId === selectedSchema?.id
  );

  return (
    <div className="min-h-screen bg-cool-gray-100 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-display font-bold text-charcoal-ink">
              Issue Credential
            </h1>
            <p className="text-warm-slate">
              Create and issue W3C-compliant verifiable credentials
            </p>
          </div>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {steps.map((step, index) => (
              <div key={index} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  index < currentStep 
                    ? 'bg-verified-green text-white' 
                    : index === currentStep 
                    ? 'bg-electric-steel text-white' 
                    : 'bg-gray-200 text-gray-500'
                }`}>
                  {index < currentStep ? <Check className="h-4 w-4" /> : index + 1}
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-16 h-0.5 mx-2 ${
                    index < currentStep ? 'bg-verified-green' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-sm text-warm-slate">
            {steps.map((step, index) => (
              <span key={index} className="text-center">{step}</span>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <Card className="credential-capital-card">
          <CardContent className="pt-6">
            {/* Step 0: Select Type */}
            {currentStep === 0 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-xl font-semibold mb-2">Select Issuance Type</h2>
                  <p className="text-warm-slate">Choose how you want to issue credentials</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card 
                    className={`cursor-pointer transition-all hover:shadow-lg ${
                      issuanceType === 'manual' ? 'border-electric-steel bg-electric-steel/5' : 'border-gray-200'
                    }`}
                    onClick={() => setIssuanceType('manual')}
                  >
                    <CardContent className="pt-6 text-center">
                      <User className="h-12 w-12 mx-auto mb-4 text-electric-steel" />
                      <h3 className="font-semibold mb-2">Manual Issuance</h3>
                      <p className="text-sm text-warm-slate">
                        Issue a single credential by entering data manually
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card 
                    className={`cursor-pointer transition-all hover:shadow-lg ${
                      issuanceType === 'bulk' ? 'border-electric-steel bg-electric-steel/5' : 'border-gray-200'
                    }`}
                    onClick={() => setIssuanceType('bulk')}
                  >
                    <CardContent className="pt-6 text-center">
                      <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 text-electric-steel" />
                      <h3 className="font-semibold mb-2">Bulk Issuance</h3>
                      <p className="text-sm text-warm-slate">
                        Issue multiple credentials by uploading a CSV file
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Step 1: Choose Schema */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-xl font-semibold mb-2">Choose Credential Schema</h2>
                  <p className="text-warm-slate">Select the schema that defines your credential structure</p>
                </div>
                
                <div className="grid gap-4">
                  {mockSchemas.map(schema => (
                    <Card 
                      key={schema.id}
                      className={`cursor-pointer transition-all hover:shadow-lg ${
                        selectedSchema?.id === schema.id ? 'border-electric-steel bg-electric-steel/5' : 'border-gray-200'
                      }`}
                      onClick={() => setSelectedSchema(schema)}
                    >
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold mb-2">{schema.name}</h3>
                            <p className="text-sm text-warm-slate mb-3">{schema.description}</p>
                            <div className="flex items-center space-x-4 text-sm text-warm-slate">
                              <span>Version: {schema.version}</span>
                              <span>Fields: {schema.fields.length}</span>
                              <span>Required: {schema.fields.filter(f => f.required).length}</span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Badge variant="outline">Active</Badge>
                            {selectedSchema?.id === schema.id && (
                              <Check className="h-5 w-5 text-verified-green" />
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                
                {errors.schema && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{errors.schema}</AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            {/* Step 2: Select Template */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-xl font-semibold mb-2">Select Template</h2>
                  <p className="text-warm-slate">Choose the visual design for your credential</p>
                </div>
                
                <div className="grid gap-4">
                  {availableTemplates.map(template => (
                    <Card 
                      key={template.id}
                      className={`cursor-pointer transition-all hover:shadow-lg ${
                        selectedTemplate?.id === template.id ? 'border-electric-steel bg-electric-steel/5' : 'border-gray-200'
                      }`}
                      onClick={() => setSelectedTemplate(template)}
                    >
                      <CardContent className="pt-6">
                        <div className="flex items-start space-x-4">
                          <div className="w-24 h-18 bg-gray-100 rounded border overflow-hidden">
                            <img 
                              src={template.backgroundImage} 
                              alt={template.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold mb-2">{template.name}</h3>
                            <p className="text-sm text-warm-slate mb-3">{template.description}</p>
                            <div className="flex items-center space-x-4 text-sm text-warm-slate">
                              <span>Size: {template.width}x{template.height}</span>
                              <span>Fields: {template.fieldPositions.length}</span>
                            </div>
                          </div>
                          {selectedTemplate?.id === template.id && (
                            <Check className="h-5 w-5 text-verified-green" />
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                
                {errors.template && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{errors.template}</AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            {/* Step 3: Enter/Upload Data */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-xl font-semibold mb-2">
                    {issuanceType === 'manual' ? 'Enter Credential Data' : 'Upload Credential Data'}
                  </h2>
                  <p className="text-warm-slate">
                    {issuanceType === 'manual' 
                      ? 'Fill in the credential information' 
                      : 'Upload a CSV file with credential data'
                    }
                  </p>
                </div>

                {issuanceType === 'manual' ? (
                  <div className="space-y-6">
                    {/* Recipient Info */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center">
                          <User className="h-5 w-5 mr-2" />
                          Recipient Information
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="recipient-name">Recipient Name *</Label>
                            <Input
                              id="recipient-name"
                              value={recipientInfo.name}
                              onChange={(e) => setRecipientInfo(prev => ({ ...prev, name: e.target.value }))}
                              className={errors.recipientName ? 'border-red-500' : ''}
                            />
                            {errors.recipientName && <p className="text-red-500 text-sm mt-1">{errors.recipientName}</p>}
                          </div>
                          <div>
                            <Label htmlFor="recipient-email">Recipient Email *</Label>
                            <Input
                              id="recipient-email"
                              type="email"
                              value={recipientInfo.email}
                              onChange={(e) => setRecipientInfo(prev => ({ ...prev, email: e.target.value }))}
                              className={errors.recipientEmail ? 'border-red-500' : ''}
                            />
                            {errors.recipientEmail && <p className="text-red-500 text-sm mt-1">{errors.recipientEmail}</p>}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="send-notification"
                            checked={recipientInfo.sendNotification}
                            onCheckedChange={(checked) => setRecipientInfo(prev => ({ ...prev, sendNotification: checked }))}
                          />
                          <Label htmlFor="send-notification">Send email notification to recipient</Label>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Credential Fields */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center">
                          <FileText className="h-5 w-5 mr-2" />
                          Credential Data
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {selectedSchema?.fields.map(field => (
                          <div key={field.name}>
                            <Label htmlFor={field.name}>
                              {field.displayName} {field.required && <span className="text-red-500">*</span>}
                            </Label>
                            {field.description && (
                              <p className="text-sm text-warm-slate mb-2">{field.description}</p>
                            )}
                            
                            {field.type === 'textarea' ? (
                              <Textarea
                                id={field.name}
                                placeholder={field.placeholder}
                                value={credentialData[field.name] || ''}
                                onChange={(e) => setCredentialData(prev => ({ ...prev, [field.name]: e.target.value }))}
                                className={errors[field.name] ? 'border-red-500' : ''}
                              />
                            ) : field.type === 'select' ? (
                              <Select
                                value={credentialData[field.name] || ''}
                                onValueChange={(value) => setCredentialData(prev => ({ ...prev, [field.name]: value }))}
                              >
                                <SelectTrigger className={errors[field.name] ? 'border-red-500' : ''}>
                                  <SelectValue placeholder={field.placeholder} />
                                </SelectTrigger>
                                <SelectContent>
                                  {field.validation?.options?.map(option => (
                                    <SelectItem key={option} value={option}>{option}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Input
                                id={field.name}
                                type={field.type}
                                placeholder={field.placeholder}
                                value={credentialData[field.name] || ''}
                                onChange={(e) => setCredentialData(prev => ({ ...prev, [field.name]: e.target.value }))}
                                className={errors[field.name] ? 'border-red-500' : ''}
                              />
                            )}
                            
                            {errors[field.name] && <p className="text-red-500 text-sm mt-1">{errors[field.name]}</p>}
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* CSV Upload */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center">
                          <Upload className="h-5 w-5 mr-2" />
                          Upload CSV File
                        </CardTitle>
                        <CardDescription>
                          Upload a CSV file with credential data. Make sure the column names match the schema fields.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex items-center space-x-4">
                          <Button
                            variant="outline"
                            onClick={generateCsvTemplate}
                            className="flex items-center"
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Download Template
                          </Button>
                          <span className="text-sm text-warm-slate">
                            Download a CSV template with the correct column structure
                          </span>
                        </div>
                        
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                          <input
                            type="file"
                            accept=".csv"
                            onChange={handleCsvUpload}
                            className="hidden"
                            id="csv-upload"
                          />
                          <label htmlFor="csv-upload" className="cursor-pointer">
                            <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                            <p className="text-sm text-gray-600">
                              Click to upload CSV file or drag and drop
                            </p>
                          </label>
                        </div>
                        
                        {bulkData.csvFile && (
                          <div className="text-sm text-verified-green">
                            <Check className="h-4 w-4 inline mr-1" />
                            {bulkData.csvFile.name} uploaded ({bulkData.parsedData.length} records)
                          </div>
                        )}
                        
                        {errors.csvFile && (
                          <Alert>
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>{errors.csvFile}</AlertDescription>
                          </Alert>
                        )}
                      </CardContent>
                    </Card>

                    {/* Preview Data */}
                    {bulkData.parsedData.length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle>Data Preview</CardTitle>
                          <CardDescription>
                            Preview of the first 5 records from your CSV file
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b">
                                  {Object.keys(bulkData.parsedData[0] || {}).map(key => (
                                    <th key={key} className="text-left p-2 font-medium">{key}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {bulkData.parsedData.slice(0, 5).map((row, index) => (
                                  <tr key={index} className="border-b">
                                    {Object.values(row).map((value: any, cellIndex) => (
                                      <td key={cellIndex} className="p-2">{value}</td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Review & Issue */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-xl font-semibold mb-2">Review & Issue</h2>
                  <p className="text-warm-slate">
                    Review the credential information before issuing
                  </p>
                </div>

                {/* Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle>Issuance Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Issuance Type</Label>
                        <p className="font-medium capitalize">{issuanceType}</p>
                      </div>
                      <div>
                        <Label>Schema</Label>
                        <p className="font-medium">{selectedSchema?.name} v{selectedSchema?.version}</p>
                      </div>
                      <div>
                        <Label>Template</Label>
                        <p className="font-medium">{selectedTemplate?.name}</p>
                      </div>
                      <div>
                        <Label>Credentials to Issue</Label>
                        <p className="font-medium">
                          {issuanceType === 'manual' ? '1' : bulkData.parsedData.length}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Preview */}
                {issuanceType === 'manual' && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Credential Preview</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="font-semibold mb-2">W3C Verifiable Credential</h3>
                        <pre className="text-sm overflow-x-auto">
                          {JSON.stringify(generateCredential(credentialData, recipientInfo.email), null, 2)}
                        </pre>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Issue Button */}
                <div className="text-center">
                  <Button
                    onClick={handleIssueCredential}
                    disabled={isProcessing}
                    className="credential-capital-button-primary"
                    size="lg"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Issue Credential{issuanceType === 'bulk' ? 's' : ''}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 0}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Previous
          </Button>
          
          {currentStep < steps.length - 1 ? (
            <Button
              onClick={handleNext}
              className="credential-capital-button-primary"
            >
              Next
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}